import { can, PortalPermissions } from "../../config/permissions";
import React, {FC, useEffect, useState} from 'react';
import {LiveTable} from "./components/liveTable/liveTable";
import FeesService from "./service/feesService";
import {
  ChannelLabel,
  Container,
  DownloadButton,
  FeeInfoText,
  TableHeader,
  ToggleButton,
  ToggleContainer, ToggleOption
} from "./styles";
import {T2} from "../../styles/titles";
import LoadingOverlay from "../../components/LoadingOverlay";
import {BillingPolicy, CreditFee, FeeData} from "./types";
import { generateFeesPdf } from './components/feesPdfReport';

const Fees: FC = () => {
  const feesService = new FeesService();
  const [tableData, setTableData] = useState<BillingPolicy[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLinkEnabled, setIsLinkEnabled] = useState<boolean>(false);
  const [captureChannel, setCaptureChannel] = useState<number>(0);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await feesService.getFees();
      return response;
    } catch (error) {
      console.log(error);
    }
    finally {
      setIsLoading(false);
    }
  }

  const formattFees = (billingPolicy: BillingPolicy) => {
    const anticipationFee = billingPolicy.anticipationFee ?? 0;
    const digitalAnticipationFee = billingPolicy.digitalAnticipationFee ?? 0;

    // Taxa de débito
    let debitFee: FeeData = {
      modality: "Débito",
      fees: {
        visa: billingPolicy.debitFeeVisaAdiq,
        mastercard: billingPolicy.debitFeeMasterCardAdiq,
        elo: billingPolicy.debitFeeEloAdiq
      },
      settlement: "D+1",
      captureChannel: 0
    };

    if (captureChannel === 1) {
      debitFee = {
        modality: "Débito",
        fees: {
          visa: 0,
          mastercard: 0,
          elo: 0
        },
        settlement: "-",
        captureChannel: 1
      };
    }

    // Separar taxas de crédito por faixa
    const creditRanges = [
      { fees: billingPolicy.creditFees.filter(cf => cf.to === 1), range: 1, start: 1, end: 1 },
      { fees: billingPolicy.creditFees.filter(cf => cf.to === 6), range: 6, start: 2, end: 6 },
      { fees: billingPolicy.creditFees.filter(cf => cf.to === 12), range: 12, start: 7, end: 12 },
      { fees: billingPolicy.creditFees.filter(cf => cf.to > 12), range: 21, start: 13, end: 21 }
    ];

    // Combinar todas as taxas
    return [
      debitFee,
      ...creditRanges.flatMap(({ fees, range, start, end }) =>
        formattCreditFee(fees, range, anticipationFee, digitalAnticipationFee, start, end)
      )
    ];
  };

  const formattCreditFee = (
    creditFees: CreditFee[],
    range: number,
    anticipationFee: number,
    digitalAnticipationFee: number,
    startInstallment: number,
    endInstallment: number
  ): FeeData[] => {
    if (creditFees.length === 0) return [];

    const settlement = anticipationFee > 0 ? "D+1" : "D+30";

    const bandeiras = [
      { id: 1, name: 'visa' },
      { id: 2, name: 'mastercard' },
      { id: 171, name: 'elo' },
      { id: 3, name: 'amex' },
      { id: 9, name: 'hipercard' }
    ];

    const createFeeForInstallment = (installment: number): FeeData => {
      const modality = installment === 1 ? "Crédito à Vista" : `Crédito ${installment}x`;
      const anticipationMultiplier = installment === 1 ? 1 : (installment / 2) + 0.5;

      const fees = bandeiras.reduce((acc, bandeira) => {
        const creditFee = creditFees.find(
          cf => cf.bandeiraId === bandeira.id && cf.captureChannel === captureChannel
        );
        const calculatedFee = (creditFee?.value ?? 0) + ((captureChannel === 0 ? anticipationFee : digitalAnticipationFee) * anticipationMultiplier);
        acc[bandeira.name] = Number(calculatedFee.toFixed(2));
        return acc;
      }, {} as Record<string, number>);

      return {
        modality,
        fees,
        settlement,
        captureChannel
      };
    };

    const result: FeeData[] = [];
    for (let i = startInstallment; i <= endInstallment; i++) {
      result.push(createFeeForInstallment(i));
    }

    return result;
  };

  const handleCaptureChannelChange = () => {
    captureChannel === 0 ? setCaptureChannel(1) : setCaptureChannel(0);
  };

  const formattScreenFees = (fee: number | null) => {
    if (!fee) return '-';
    return `${fee.toString().replace('.', ',')}%`;
  };

  useEffect(() => {
    fetchData()
      .then(response => {
        if (response && response.records.length > 0) setTableData(response.records);
      })
  }, []);

  useEffect(() => {
    if (tableData) {
      const creditFees = tableData[0].creditFees;
      creditFees.forEach(cf => {
        if (cf.captureChannel === 1) setIsLinkEnabled(true);
      })
    }
  }, [tableData]);

  return (
    <Container>
      <LoadingOverlay show={isLoading} />
      <T2>Taxas</T2>
      {tableData && (
        <div>
          {can(PortalPermissions.financeiro.taxas.baixarTabela) && (
            <DownloadButton
              onClick={() =>
                generateFeesPdf(
                  formattFees(tableData[0]),
                  tableData[0].anticipationFee ?? 0,
                  tableData[0].digitalAnticipationFee ?? 0,
                  captureChannel === 0 ? 'POS' : 'DIGITAL'
                )
              }
            >
              Download
            </DownloadButton>
          )}
          <div style={
            {
              display: "flex",
              justifyContent: "center"
            }
          }>
            <TableHeader>
              <FeeInfoText>
                Taxa de antecipação {captureChannel === 1 && 'e-commerce'}:
                <span>
                  {formattScreenFees(
                    captureChannel === 0
                      ? (tableData[0].anticipationFee > 0 ? tableData[0].anticipationFee : 0)
                      : (tableData[0].digitalAnticipationFee > 0 ? tableData[0].digitalAnticipationFee : 0)
                  )}
                </span>
              </FeeInfoText>

              {isLinkEnabled && (
                <ToggleContainer>
                  <ToggleOption
                    isActive={captureChannel === 0}
                    onClick={() => setCaptureChannel(0)}
                  >
                    💳 POS
                  </ToggleOption>
                  <ToggleOption
                    isActive={captureChannel === 1}
                    onClick={() => setCaptureChannel(1)}
                  >
                    🛒 E-commerce
                  </ToggleOption>
                </ToggleContainer>
              )}
            </TableHeader>
          </div>

          <LiveTable
            data={formattFees(tableData[0])}
            captureChannel={captureChannel}
            anticipationFee={
              captureChannel === 0
                ? tableData[0].anticipationFee
                : 0
            }
            digitalAnticipationFee={
              captureChannel > 0
                ? tableData[0].digitalAnticipationFee
                : 0
            } />
        </div>
      )}
    </Container>
  )
}

export default Fees;
